import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaFolder, FaCheckCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { listJobs } from '../api/api';
import RefreshButton from '../components/RefreshButton';

const Jobs = ({ setAlert }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load initial jobs
  const loadJobs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listJobs();
      setJobs(data.jobs || []);
      setNextToken(data.nextToken || null);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load more jobs
  const loadMore = useCallback(async () => {
    if (!nextToken || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const data = await listJobs({ nextToken });
      setJobs(prev => [...prev, ...(data.jobs || [])]);
      setNextToken(data.nextToken || null);
    } catch (err) {
      console.error('Error loading more jobs:', err);
      setAlert({
        type: 'error',
        message: 'Failed to load more jobs'
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextToken, isLoadingMore, setAlert]);

  // Load jobs on mount
  React.useEffect(() => {
    if (!hasLoaded) {
      loadJobs();
    }
  }, [hasLoaded, loadJobs]);

  // Refresh function
  const mutate = useCallback(() => {
    loadJobs();
  }, [loadJobs]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FaCheckCircle className="mr-1" />
            Completed
          </span>
        );
      case 'Processing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <FaClock className="mr-1" />
            Processing
          </span>
        );
      case 'Partial':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <FaClock className="mr-1" />
            Partial
          </span>
        );
      case 'Failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <FaExclamationTriangle className="mr-1" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const handleJobClick = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading jobs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-red-50">
        <p className="text-red-700">Failed to load jobs. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Jobs</h1>
          <p className="text-gray-600 mt-1">
            View and manage your upload job batches
          </p>
        </div>
        <RefreshButton mutate={mutate} />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{jobs.length}</p>
            </div>
            <FaFolder className="text-3xl text-cyan-600" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Assessed</p>
              <p className="text-2xl font-bold text-green-600">
                {jobs.filter(j => j.status === 'Completed').length}
              </p>
            </div>
            <FaCheckCircle className="text-3xl text-green-600" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Awaiting assessment</p>
              <p className="text-2xl font-bold text-blue-600">
                {jobs.filter(j => j.status === 'Processing' || j.status === 'Partial').length}
              </p>
            </div>
            <FaClock className="text-3xl text-blue-600" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600">
                {jobs.filter(j => j.status === 'Failed').length}
              </p>
            </div>
            <FaExclamationTriangle className="text-3xl text-red-600" />
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Jobs</h2>
        {jobs.length === 0 ? (
          <div className="text-center py-12">
            <FaFolder className="mx-auto text-5xl text-gray-400 mb-4" />
            <p className="text-gray-600">No jobs found</p>
            <p className="text-sm text-gray-500 mt-2">
              Upload some recordings to see your jobs here
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Upload Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uploaded By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assessed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => (
                  <tr
                    key={job.jobId}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleJobClick(job.jobId)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {job.jobId === 'legacy' ? 'Legacy' : job.jobId.substring(0, 20) + '...'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(job.uploadDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {job.campaign}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {job.template}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.uploadedBy}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {job.completedCount}/{job.fileCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(job.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-cyan-600 hover:text-cyan-800">
                      View Details →
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Load More Button */}
            {nextToken && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-center">
                <button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingMore ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More Jobs'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Jobs;
